// src/views/StakeView/index.jsx - Clean Whale + Regular User Strategy
import React, { useState, useEffect } from 'react';
import { Wallet, Camera, RefreshCw } from 'lucide-react';
import { CONFIG, ABIS } from '../../config/constants';
import { toNum, toNumArray } from '../../utils/bigIntUtils';

// Component imports
import { UniversalControlBar } from './UniversalControlBar';
import { NFTGrid } from './NFTGrid';
import { ActionBar } from './ActionBar';

const MAX_BATCH_SIZE = 100;

// Simple disconnected view
const ConnectWalletView = ({ connectWallet }) => (
  <div>
    <h2 className="text-3xl md:text-5xl font-black mb-8 text-white font-mono uppercase">
      STAKE YOUR NFTS
    </h2>
    <div className="text-center py-20 text-xl p-8 bg-gradient-to-br from-black to-gray-900 border-4 border-white/90 shadow-[0_0_20px_rgba(0,255,0,0.2)]">
      <Wallet size={48} className="mx-auto mb-4 opacity-50" />
      <div className="font-bold mb-2">WALLET NOT CONNECTED</div>
      <div className="text-sm opacity-75 mb-4">Connect your wallet to view your NFTs</div>
      <button 
        onClick={connectWallet}
        className="bg-gradient-to-r from-white to-gray-300 text-black font-bold py-2 px-6 border-2 border-green-400/90 hover:opacity-90 transition-all shadow-[0_0_10px_rgba(0,255,0,0.3)]"
      >
        CONNECT WALLET
      </button>
    </div>
  </div>
);

// Loading state
const LoadingState = () => (
  <div className="text-center py-20 p-8 bg-gradient-to-br from-black to-gray-900 border-4 border-white/90">
    <RefreshCw size={48} className="animate-spin mx-auto mb-4" />
    <div className="text-xl font-bold mb-2">LOADING YOUR NFTS</div>
    <div className="text-sm opacity-75">Fetching from blockchain...</div>
  </div>
);

// No NFTs state
const NoNFTsState = ({ setView }) => (
  <div className="text-center py-20 text-xl p-8 bg-gradient-to-br from-black to-gray-900 border-4 border-white/90">
    <Camera size={48} className="mx-auto mb-4 opacity-50" />
    <div className="font-bold mb-2">NO NFTS FOUND</div>
    <div className="text-sm opacity-75 mb-4">You don't own any Dunk Poge NFTs yet</div>
    <button 
      onClick={() => setView('mint')}
      className="bg-gradient-to-r from-green-400 to-green-600 text-black font-bold py-2 px-6 border-2 border-black hover:opacity-90"
    >
      GO TO MINT PAGE
    </button>
  </div>
);

export const StakeView = ({ 
  isConnected, 
  connectWallet, 
  nftBalance, 
  stakedTokens, 
  isApproved, 
  loading, 
  approveStaking,
  revokeApproval,
  stake,
  unstake,
  publicClient,
  account,
  setView,
  emissionRate
}) => {
  const [selectedNFTs, setSelectedNFTs] = useState([]);
  const [userNFTs, setUserNFTs] = useState([]);
  const [loadingNFTs, setLoadingNFTs] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);

  // Your working NFT fetch logic - with BigInt safety
  const refreshUserNFTs = async () => {
    if (!isConnected || !publicClient || !account || loadingNFTs) return;

    setLoadingNFTs(true);
    try {
      const [walletTokenIds, userStakeInfo] = await Promise.all([
        publicClient.readContract({
          address: CONFIG.ADDRESSES.NFT,
          abi: ABIS.NFT,
          functionName: 'tokensOfOwner',
          args: [account]
        }).catch(() => []),
        
        publicClient.readContract({
          address: CONFIG.ADDRESSES.STAKING,
          abi: ABIS.STAKING,
          functionName: 'getUserStakeInfo',
          args: [account]
        }).catch(() => [[], 0n, 0n])
      ]);
      
      const allTokenIds = new Set();
      
      if (walletTokenIds && Array.isArray(walletTokenIds)) {
        walletTokenIds.forEach(id => allTokenIds.add(toNum(id)));
      }
      
      const stakedTokenIds = userStakeInfo[0] || [];
      if (Array.isArray(stakedTokenIds)) {
        stakedTokenIds.forEach(id => allTokenIds.add(toNum(id)));
      }
      
      const tokenIds = Array.from(allTokenIds);
      
      if (tokenIds.length === 0) {
        setUserNFTs([]);
        setLoadingNFTs(false);
        return;
      }
      
      const allNFTs = [];
      const batchSize = 3;
      
      for (let i = 0; i < tokenIds.length; i += batchSize) {
        const batch = tokenIds.slice(i, i + batchSize);
        
        const batchPromises = batch.map(async (tokenId) => {
          try {
            const tokenIdBigInt = BigInt(tokenId);
            const isStaked = stakedTokenIds.map(id => toNum(id)).includes(tokenId);
            
            const tokenURI = await publicClient.readContract({
              address: CONFIG.ADDRESSES.NFT,
              abi: ABIS.NFT,
              functionName: 'tokenURI',
              args: [tokenIdBigInt]
            });
            
            let svgContent = '';
            let metadata = null;
            
            if (tokenURI.startsWith('data:application/json;base64,')) {
              try {
                const base64Json = tokenURI.replace('data:application/json;base64,', '');
                const jsonString = atob(base64Json);
                metadata = JSON.parse(jsonString);
                
                const imageData = metadata.image;
                if (imageData.startsWith('data:image/svg+xml;base64,')) {
                  const base64Svg = imageData.replace('data:image/svg+xml;base64,', '');
                  svgContent = atob(base64Svg);
                } else {
                  svgContent = imageData;
                }
                
                let stakeInfo = null;
                if (isStaked) {
                  try {
                    const stakeData = await publicClient.readContract({
                      address: CONFIG.ADDRESSES.STAKING,
                      abi: ABIS.STAKING,
                      functionName: 'getStakeInfo',
                      args: [tokenIdBigInt]
                    });
                    stakeInfo = toNumArray(stakeData);
                  } catch (error) {
                    console.error(`Error fetching stake info for token ${tokenId}:`, error);
                  }
                }
                
                return {
                  tokenId: tokenId,
                  svg: svgContent,
                  isStaked: isStaked,
                  stakeInfo: stakeInfo,
                  name: metadata.name || `Dunk Poge #${tokenId}`,
                  metadata: metadata,
                  seed: metadata.seed || null
                };
                
              } catch (error) {
                console.error(`Error processing metadata for token ${tokenId}:`, error);
              }
            }
            
            return {
              tokenId: tokenId,
              svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96"><rect width="96" height="96" fill="#222"/><text x="48" y="48" text-anchor="middle" fill="#fff" font-size="10">Dunk Poge #${tokenId}</text></svg>`,
              isStaked: isStaked,
              stakeInfo: null,
              name: `Dunk Poge #${tokenId}`,
              metadata: null,
              seed: null
            };
            
          } catch (error) {
            console.error(`Error processing token ${tokenId}:`, error);
            return null;
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        const validResults = batchResults.filter(nft => nft != null && nft.tokenId != null);
        allNFTs.push(...validResults);
        
        if (i + batchSize < tokenIds.length) {
          setUserNFTs([...allNFTs]);
        }
      }
      
      setUserNFTs(allNFTs);
      setLastFetchTime(Date.now());
      
    } catch (error) {
      console.error('Error refreshing NFTs:', error);
      setUserNFTs([]);
    } finally {
      setLoadingNFTs(false);
    }
  };

  useEffect(() => {
    if (!isConnected || !publicClient || !account) {
      setUserNFTs([]);
      setLoadingNFTs(false);
      return;
    }

    if (lastFetchTime === 0) {
      refreshUserNFTs();
    }
  }, [isConnected, publicClient, account]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isConnected || userNFTs.length === 0) return;
      
      if (e.key === 'Escape' && selectedNFTs.length > 0) {
        deselectAll();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [userNFTs, selectedNFTs, isConnected]);

  // Selection helpers
  const getUnstakedNFTs = () => userNFTs.filter(nft => nft && !nft.isStaked);
  const getStakedNFTs = () => userNFTs.filter(nft => nft && nft.isStaked);

  // Calculate selected types
  const selectedStakedNFTs = selectedNFTs.filter(tokenId => {
    const nft = userNFTs.find(n => n.tokenId === tokenId);
    return nft?.isStaked;
  });
  
  const selectedUnstakedNFTs = selectedNFTs.filter(tokenId => {
    const nft = userNFTs.find(n => n.tokenId === tokenId);
    return nft && !nft.isStaked;
  });

  // Selection functions
  const toggleNFT = (tokenId) => {
    const nft = userNFTs.find(n => n && n.tokenId === tokenId);
    if (!nft) return;
    
    if (!selectedNFTs.includes(tokenId)) {
      if (selectedNFTs.length >= MAX_BATCH_SIZE) return;
      
      if (selectedNFTs.length > 0) {
        const firstSelected = userNFTs.find(n => n.tokenId === selectedNFTs[0]);
        if (firstSelected && nft.isStaked !== firstSelected.isStaked) return;
      }
    }
    
    setSelectedNFTs(prev => 
      prev.includes(tokenId) 
        ? prev.filter(id => id !== tokenId) 
        : [...prev, tokenId]
    );
  };
  
  const deselectAll = () => setSelectedNFTs([]);

  // Update NFT status without full reload
  const updateNFTStakeStatus = (tokenIds, isNowStaked) => {
    setUserNFTs(prevNFTs => 
      prevNFTs.map(nft => 
        tokenIds.includes(nft.tokenId) 
          ? { ...nft, isStaked: isNowStaked, stakeInfo: null }
          : nft
      )
    );
  };

  // WHALE ACTIONS - Stake/Unstake ALL
  const handleStakeAll = async () => {
    const unstakedNFTs = getUnstakedNFTs();
    if (unstakedNFTs.length === 0) return;
    
    // Process in batches of 100
    for (let i = 0; i < unstakedNFTs.length; i += MAX_BATCH_SIZE) {
      const batch = unstakedNFTs.slice(i, i + MAX_BATCH_SIZE);
      const batchIds = batch.map(nft => nft.tokenId);
      await stake(batchIds);
      updateNFTStakeStatus(batchIds, true);
    }
    
    deselectAll();
  };

  const handleUnstakeAll = async () => {
    const stakedNFTs = getStakedNFTs();
    if (stakedNFTs.length === 0) return;
    
    // Process in batches of 100
    for (let i = 0; i < stakedNFTs.length; i += MAX_BATCH_SIZE) {
      const batch = stakedNFTs.slice(i, i + MAX_BATCH_SIZE);
      const batchIds = batch.map(nft => nft.tokenId);
      await unstake(batchIds);
      updateNFTStakeStatus(batchIds, false);
    }
    
    deselectAll();
  };

  // EMERGENCY UNSTAKE - Same as unstake all but with warning built into UI
  const handleEmergencyUnstake = async () => {
    const stakedNFTs = getStakedNFTs();
    if (stakedNFTs.length === 0) return;
    
    // Process in batches of 100
    for (let i = 0; i < stakedNFTs.length; i += MAX_BATCH_SIZE) {
      const batch = stakedNFTs.slice(i, i + MAX_BATCH_SIZE);
      const batchIds = batch.map(nft => nft.tokenId);
      await unstake(batchIds);
      updateNFTStakeStatus(batchIds, false);
    }
    
    deselectAll();
  };

  // REGULAR USER ACTIONS - Selected NFTs only
  const handleStake = async () => {
    if (selectedUnstakedNFTs.length === 0) return;
    await stake(selectedUnstakedNFTs);
    updateNFTStakeStatus(selectedUnstakedNFTs, true);
    selectedUnstakedNFTs.forEach(id => toggleNFT(id));
  };

  const handleUnstake = async () => {
    if (selectedStakedNFTs.length === 0) return;
    await unstake(selectedStakedNFTs);
    updateNFTStakeStatus(selectedStakedNFTs, false);
    selectedStakedNFTs.forEach(id => toggleNFT(id));
  };

  if (!isConnected) {
    return <ConnectWalletView connectWallet={connectWallet} />;
  }

  return (
    <div>
      {/* Universal Control Bar with Whale Buttons */}
      <UniversalControlBar
        stakedCount={getStakedNFTs().length}
        unstakedCount={getUnstakedNFTs().length}
        selectedCount={selectedNFTs.length}
        onSync={refreshUserNFTs}
        isLoading={loadingNFTs}
        onStakeAll={handleStakeAll}
        onUnstakeAll={handleUnstakeAll}
        isApproved={isApproved}
        loading={loading}
        approveStaking={approveStaking}
        revokeApproval={revokeApproval}
        onEmergencyUnstake={handleEmergencyUnstake}
      />

      {loadingNFTs && userNFTs.length === 0 ? (
        <LoadingState />
      ) : userNFTs.length === 0 ? (
        <NoNFTsState setView={setView} />
      ) : (
        <>
          {/* Action Bar for Selected NFTs (Sticky Top) */}
          <ActionBar
            selectedNFTs={selectedNFTs}
            selectedStakedNFTs={selectedStakedNFTs}
            selectedUnstakedNFTs={selectedUnstakedNFTs}
            isApproved={isApproved}
            loading={loading}
            handleStake={handleStake}
            handleUnstake={handleUnstake}
            approveStaking={approveStaking}
            emissionRate={emissionRate}
            onDeselectAll={deselectAll}
          />

          {/* NFT Grid */}
          <NFTGrid
            userNFTs={userNFTs}
            selectedNFTs={selectedNFTs}
            onToggleSelect={toggleNFT}
            emissionRate={emissionRate}
          />
        </>
      )}
    </div>
  );
};